import { XMarkIcon } from "@heroicons/react/20/solid";
import { format, parse } from "date-fns";
import { merge, setWith } from "lodash";
import React, { useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { NotificationManager } from "react-notifications";
import { connect } from "react-redux";
import ReactSelect from "react-select";
import { Actions as ACTIONS } from "../../../../store/actions/employeeActions";
import { DateInputFormat, handleRawDate } from "../../../common/DateComponents";
import { toTitleCase } from "../../../common/validation/Validators";
import Tooltip from "@/tooltip";
import Department from "./modules/Department";
import PostponementDate from "./modules/PostponementDate";
import NewOccupation from "../../occupations/NewOccupation";

const UpdateEmployeeModal = ({
	isModalOpen,
	tab,
	minDate,
	maxDate,
	closeModal,
	modalSaving,
	onlyReplace,
	employee,
	updateEmployeeDetails,
	existingList,
	params,
	noDatePrompt,
	onlyNew,
}) => {
	const [confirm, setConfirm] = useState();
	const [data, setData] = useState({});
	const [date, setDate] = useState();

	useEffect(() => {
		if (isModalOpen) return;
		setData({});
		setConfirm();
	}, [isModalOpen]);

	if (!isModalOpen) return null;

	const updateDetails = () => {
		const body = { ...params, ...data };
		if (onlyReplace) {
			updateEmployeeDetails({ replace: true, tab, data: body });
			return;
		}
		if (onlyNew && noDatePrompt) {
			updateEmployeeDetails({ replace: false, effectiveFrom: "null", tab, data: body });
			return
		}
		switch (confirm) {
			default:
				setConfirm("confirm");
				break;
			case "new":
				if (!date && !noDatePrompt) NotificationManager.error("Please select a date", "Error");
				else updateEmployeeDetails({ replace: false, effectiveFrom: date, tab, data: body });
				break;
			case "replace":
				updateEmployeeDetails({ replace: true, tab, data: body });
				break;
		}
	};

	const updateField = (field, value) => {
		// console.debug(field, value)
		if (!field) return;
		if (field.includes("[")) field = setWith({}, field, value, Object);

		const newData = merge(data, { ...(typeof field == "object" ? field : { [field]: value }) });

		setData({ ...newData });
	};

	const ModalPage = () => {
		switch (confirm) {
			case "confirm":
				return (
					<>
						<div className="block-content">
							<p>
								You're about to update {employee?.givenName}'s {tab}. You can either replace the
								previous values, or have the change take effect on another date.
							</p>

							<div
								style={{
									display: "flex",
									flexDirection: "row",
								}}
							>
								<div
									style={{
										width: "40%",
										textAlign: "center",
									}}
								>
									<Tooltip
										content={`You can only replace if you have existing ${tab} details`}
										isOpen={existingList?.length ? false : undefined}
									>
										<button
											className="btn btn-success"
											onClick={() => setConfirm("replace")}
											disabled={modalSaving || existingList?.length == 0}
										>
											Replac{modalSaving ? `ing ${toTitleCase(tab)}...` : `e ${toTitleCase(tab)}`}
										</button>
									</Tooltip>
								</div>
								<div
									style={{
										flexGrow: 2,
										alignSelf: "center",
									}}
								>
									<div
										style={{
											display: "flex",
											flexDirection: "row",
											alignItems: "center",
										}}
									>
										<hr
											style={{
												flexGrow: 1,
												margin: 5,
											}}
										/>
										<span>or</span>
										<hr
											style={{
												flexGrow: 1,
												margin: 5,
											}}
										/>
									</div>
								</div>

								<div
									style={{
										width: "40%",
										textAlign: "center",
									}}
								>
									<button
										className="btn btn-success"
										disabled={modalSaving}
										onClick={() => {
											if (!noDatePrompt) setConfirm("new");
											else updateDetails();
										}}
									>
										Create New {toTitleCase(tab)}
									</button>
								</div>
							</div>
						</div>
					</>
				);
			case "new":
				return (
					<>
						<div className="block-content form-container">
							<p>
								From when would you like {employee.givenName}'s new {tab} to take effect?
							</p>

							<ReactDatePicker
								dateFormat="dd MMM yyyy"
								className="form-control form-input"
								wrapperClassName="full-width"
								customInput={<DateInputFormat />}
								minDate={minDate ? parse(minDate, "yyyy-MM-dd", new Date()) : null}
								maxDate={maxDate ? parse(maxDate, "yyyy-MM-dd", new Date()) : null}
								selected={date ? parse(date, "yyyy-MM-dd", new Date()) : null}
								autoComplete="off"
								onChange={(newDate) => {
									setDate(format(newDate, "yyyy-MM-dd"));
								}}
								onChangeRaw={(e) =>
									handleRawDate(e.target.value, (newDate) => {
										setDate(format(newDate, "yyyy-MM-dd"));
									})
								}
								strictParsing
							/>
						</div>
					</>
				);
			case "replace":
				return (
					<>
						<div className="block-content">
							<p>
								You are chosing to replace {employee.givenName}'s previous {tab}. Be aware that if you
								have already payslips using the current setting, these may have different values upon
								recalculation.
							</p>
							<div className="clearfix" />
						</div>
					</>
				);
		}

		switch (tab) {
			case "department":
				return (
					<Department
						updateField={(field, value) => updateField(field, value)}
						data={data}
					/>
				);
			case "occupation":
				return <NewOccupation />;
			case "Enrolment Postponement":
				return (
					<PostponementDate
						updateField={(field, value) => updateField(field, value)}
						data={data}
					/>
				);
		}

		return null;
	};

	return (
		<div
			className="modal"
			tabIndex="-1"
			role="dialog"
			aria-hidden="true"
			style={{ display: "block", background: "rgba(0,0,0,0.4)", overflow: "auto" }}
		>
			<div
				className="modal-dialog"
				style={{ width: "40%" }}
			>
				<div className="modal-content">
					<div
						className="block block-themed block-transparent remove-margin-b"
						style={{ padding: 0 }}
					>
						<div
							className="block-header bg-primary-dark"
							style={{ padding: 20, position: "sticky", top: 0, zIndex: 50 }}
						>
							<ul className="block-options">
								<li>
									<button
										type="button"
										onClick={closeModal}
									>
										<XMarkIcon className="h-6 w-6 mr-3 text-white" />
									</button>
								</li>
							</ul>
							<h3 className="block-title">Update {tab} details</h3>
						</div>

						<div className="block-content">
							<ModalPage />
							<div className="col-xs-12 form-container">
								{confirm && confirm !== "confirm" ? (
									<button
										className="btn btn-danger pull-left"
										onClick={() => setConfirm("confirm")}
										style={{ marginLeft: 0 }}
										disabled={modalSaving}
									>
										Go Back
									</button>
								) : null}
								{confirm !== "confirm" ? (
									<button
										className="pull-right btn btn-success"
										onClick={() => updateDetails()}
										disabled={modalSaving}
									>
										{confirm
											? `Updat${modalSaving ? "ing" : "e"} ${toTitleCase(tab)} ${
													modalSaving ? "..." : ""
											  }`
											: "Save"}
									</button>
								) : null}
							</div>
						</div>
						<div className="clearfix"></div>
					</div>
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => {
	const { saving, existingList, minDate, maxDate, onlyReplace, tab, params, noDatePrompt, onlyNew } =
		state.employee?.updateDetailsModal || {};

	return {
		isModalOpen: !!state.employee?.updateDetailsModal,
		modalSaving: saving,
		existingList,
		minDate,
		maxDate,
		onlyReplace,
		employee: state.employee?.agreementInfo?.employeeDetails,
		tab,
		params,
		noDatePrompt,
		onlyNew,
	};
};
const mapDispatchToProps = (dispatch) => ({
	closeModal: () => dispatch(ACTIONS.updateDetailsModal({})),
	updateEmployeeDetails: ({ replace, effectiveFrom, tab, data }) =>
		dispatch(ACTIONS.updateDetails({ replace, effectiveFrom, tab, data })),
});

export default connect(mapStateToProps, mapDispatchToProps)(UpdateEmployeeModal);
